import React, { useState } from "react";
import {
  Line,
  LineChart,
  Customized,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import {
  formatSeriesValue,
  TimelinePoint,
  TimelineSegment,
  TimelineSeries,
  withUnit,
} from "./timelineSeries";
import { formatTimelineDate, TimelineWindow, xPct } from "./timeScale";
import TimelineTooltip, { TooltipEntry } from "./TimelineTooltip";
import { Tooltip } from "@mui/material";
import { RangeBars } from "../../score/CustomLineChart";
import InfoIcon from "../../../svg/InfoIcon";
import classes from "./Timeline.module.css";

type Props = {
  series: TimelineSeries;
  window: TimelineWindow;
  showGoal: boolean;
  /** The row's floor; TimelineView lowers it in full screen on short windows. */
  minHeight?: number;
};

export const METRIC_ROW_HEIGHT = 93;
// The least a row can hold: the name and two meta lines with their padding.
export const METRIC_ROW_MIN_HEIGHT = 70;
// Same arrows as the biomarker cards' change (TrendSparkline).
const DELTA_ARROWS = { up: "↑", down: "↓", flat: "→" };
// Plot padding, so the top and bottom dots are not cut in half.
const PLOT_PAD = 8;

/**
 * One stacked metric: name, range and change on the left; the readings over
 * the window on the right, on the series' own Y scale, over its range bands.
 *
 * The X axis is numeric (epoch ms) over exactly the window, the same scale
 * the tests and service rows use. The readings kept just outside the window
 * are moved onto its edges (`clipToWindow`), so the line runs in from the
 * edge without Recharts' overflow clipping, which would also cut the dots.
 */
/**
 * Replaces each out-of-window neighbour with the point where the line
 * crosses the window's edge. With nothing plotted outside the domain, the
 * chart needs no clip path, and dots at the top or bottom stay whole.
 */
const clipToWindow = (points: TimelinePoint[], window: TimelineWindow) =>
  points.flatMap((point, index): TimelinePoint[] => {
    if (point.inWindow) return [point];
    const before = point.t < window.startMs;
    const edge = before ? window.startMs : window.endMs;
    const other = points[before ? index + 1 : index - 1];
    if (!other || point.value === null || other.value === null) return [];
    const ratio = (edge - point.t) / (other.t - point.t);
    return [
      {
        ...point,
        t: edge,
        value: point.value + (other.value - point.value) * ratio,
        // Only where the line crosses the edge: never a reading, so no bar.
        range: undefined,
      },
    ];
  });

/** The row's reference ranges, one line each: swatch, category, bounds. */
const SegmentsLegend = ({ segments }: { segments: TimelineSegment[] }) => {
  const theme: Theme = useTheme();
  return (
    <div style={styles.legend}>
      <div style={styles.legendTitle(theme)}>Reference ranges</div>
      {segments.map((segment, index) => (
        <div key={index} style={styles.legendRow}>
          <span style={styles.legendSwatch(segment.color)} />
          <span style={styles.legendLabel(theme)}>{segment.label}</span>
          <span style={styles.legendRange(theme)}>{segment.range}</span>
        </div>
      ))}
    </div>
  );
};

const MetricRow = ({
  series,
  window,
  showGoal,
  minHeight = METRIC_ROW_HEIGHT,
}: Props) => {
  const theme: Theme = useTheme();
  // A reading and its day's target are separate dots with separate tooltips.
  const [hovered, setHovered] = useState<{
    point: TimelinePoint;
    cy: number;
    kind: "value" | "target";
  } | null>(null);
  const [bottomTick, topTick] = series.yTicks;

  const renderDot = (props: any) => {
    const { cx, cy, payload, index } = props;
    const point = payload as TimelinePoint;
    if (
      !point?.inWindow ||
      point.value === null ||
      // A range reading is its hatched bar (RangeBars); the line meets it.
      point.range ||
      typeof cx !== "number" ||
      typeof cy !== "number"
    ) {
      return <g key={`dot-${index}`} />;
    }
    const isHovered = hovered?.kind === "value" && hovered.point.t === point.t;
    return (
      <g
        key={`dot-${index}`}
        onMouseEnter={() => setHovered({ point, cy, kind: "value" })}
        onMouseLeave={() => setHovered(null)}
        style={{ cursor: "default" }}
      >
        <circle cx={cx} cy={cy} r={10} fill="transparent" />
        <circle cx={cx} cy={cy} r={isHovered ? 5 : 3} fill={point.color} />
      </g>
    );
  };

  const renderTargetDot = (props: any) => {
    const { cx, cy, payload, index } = props;
    const point = payload as TimelinePoint;
    if (
      typeof point?.target !== "number" ||
      typeof cx !== "number" ||
      typeof cy !== "number"
    ) {
      return <g key={`target-${index}`} />;
    }
    const isHovered = hovered?.kind === "target" && hovered.point.t === point.t;
    return (
      <g
        key={`target-${index}`}
        onMouseEnter={() => setHovered({ point, cy, kind: "target" })}
        onMouseLeave={() => setHovered(null)}
        style={{ cursor: "default" }}
      >
        <circle cx={cx} cy={cy} r={8} fill="transparent" />
        <circle
          cx={cx}
          cy={cy}
          r={isHovered ? 4 : 2}
          fill={theme.colorTokens.N[70]}
        />
      </g>
    );
  };

  const tooltip = (() => {
    if (!hovered) return null;
    const { point, kind } = hovered;
    if (kind === "target" && typeof point.target === "number") {
      return {
        title: `${series.label} Target`,
        lines: [
          withUnit(formatSeriesValue(point.target, series.unit), series.unit),
          formatTimelineDate(point.t),
        ],
      };
    }
    return {
      title: series.label,
      // Biomarkers add their category and its range between value and date.
      lines: [point.display, point.category, formatTimelineDate(point.t)].filter(
        (line): line is string => Boolean(line)
      ),
    };
  })();

  return (
    <div
      className={`${classes.row} ${classes.rowDivider}`}
      // The metrics block is always as tall as five rows (TimelineView), and
      // the selected rows share it equally: one metric gets the whole block,
      // two get half each, five get a row each. minHeight is a floor, not a
      // fixed height: a wrapped name still makes its row taller.
      style={{ flex: "1 1 0", minHeight }}
    >
      <div className={classes.label}>
        <div style={styles.name(theme)}>{series.label}</div>
        {series.subtitle && (
          <div style={styles.subtitleRow}>
            <div style={styles.meta(theme)}>{series.subtitle}</div>
            {/* The bands behind the line, named and bounded: an info icon
              on the normal-range line, so the plot's hover stays with the
              readings. */}
            {series.segments && series.segments.length > 0 && (
              // MUI's Tooltip directly, styled like CustomTooltip's "medium"
              // card: CustomTooltip only renders string (HTML) messages.
              <Tooltip
                arrow
                placement="right"
                title={<SegmentsLegend segments={series.segments} />}
                slotProps={{
                  tooltip: { sx: styles.legendCard(theme) },
                  arrow: { sx: styles.legendArrow(theme) },
                }}
              >
                <span style={styles.infoIcon}>
                  <InfoIcon />
                </span>
              </Tooltip>
            )}
          </div>
        )}
        {series.delta && (
          <div style={styles.meta(theme)}>
            {DELTA_ARROWS[series.delta.direction]}
            {series.delta.text}
          </div>
        )}
        {topTick && (
          <span
            className={classes.yTick}
            style={{ ...styles.tick(theme), top: PLOT_PAD - 5 }}
          >
            {topTick.label}
          </span>
        )}
        {bottomTick && (
          <span
            className={classes.yTick}
            style={{ ...styles.tick(theme), bottom: PLOT_PAD - 5 }}
          >
            {bottomTick.label}
          </span>
        )}
      </div>

      <div className={classes.plot}>
        {series.unavailable ? (
          <div style={styles.unavailable(theme)}>{series.unavailable}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={clipToWindow(series.points, window)}
              margin={{ top: PLOT_PAD, right: 0, bottom: PLOT_PAD, left: 0 }}
            >
              <XAxis
                dataKey="t"
                type="number"
                domain={[window.startMs, window.endMs]}
                hide
              />
              <YAxis domain={series.domain} hide />
              {series.bands?.map((band, index) => (
                <ReferenceArea
                  key={index}
                  y1={band.from ?? series.domain[0]}
                  y2={band.to ?? series.domain[1]}
                  fill={band.color}
                  fillOpacity={1}
                  stroke="none"
                  ifOverflow="hidden"
                />
              ))}
              <Customized
                component={
                  <RangeBars
                    chartId={series.key}
                    onHover={(payload: TimelinePoint | null, yTop: number) =>
                      setHovered(
                        payload
                          ? { point: payload, cy: yTop, kind: "value" }
                          : null
                      )
                    }
                  />
                }
              />
              <Line
                dataKey="value"
                type="linear"
                stroke={theme.colorTokens.N[60]}
                strokeWidth={1}
                dot={renderDot}
                activeDot={false}
                connectNulls
                isAnimationActive={false}
              />
              {/* The goal, day by day — dashed, with its own small dots, as the
                dashboard chart drew it. */}
              {showGoal && (
                <Line
                  dataKey="target"
                  type="linear"
                  // Figma 16508:64672: 1px, dashed 2 2, #9A9EA3 (N[70] is the
                  // nearest token, #9A9EAE).
                  stroke={theme.colorTokens.N[70]}
                  strokeWidth={1}
                  strokeDasharray="2 2"
                  dot={renderTargetDot}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}

        {hovered && tooltip && (
          <TimelineTooltip
            leftPct={xPct(hovered.point.t, window)}
            anchorY={hovered.cy - 5}
          >
            <TooltipEntry title={tooltip.title} lines={tooltip.lines} />
          </TimelineTooltip>
        )}
      </div>
    </div>
  );
};

export default MetricRow;

const styles = {
  subtitleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
  },
  infoIcon: {
    display: "inline-flex",
    // Centred on the subtitle's 16px line.
    height: "16px",
    alignItems: "center",
    cursor: "default",
  },
  // CustomTooltip's medium card: P[10] background, 8px radius, 8px 12px.
  legendCard: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.P[10],
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    borderRadius: "8px",
    padding: "8px 12px",
    boxShadow: "var(--box-shadow-general)",
    maxWidth: 320,
  }),
  legendArrow: (theme: Theme) => ({
    color: theme.colorTokens.P[10],
  }),
  legend: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
  },
  legendTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    marginBottom: "2px",
  }),
  legendRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  legendSwatch: (color: string) => ({
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: color,
    flexShrink: 0,
  }),
  legendLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    whiteSpace: "nowrap" as "nowrap",
  }),
  legendRange: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
  }),
  name: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    // Long lab names wrap rather than truncate; the row grows to fit.
    overflowWrap: "anywhere" as "anywhere",
  }),
  meta: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    // pre-wrap keeps the double space before "since" and still wraps.
    whiteSpace: "pre-wrap" as "pre-wrap",
    overflowWrap: "anywhere" as "anywhere",
  }),
  tick: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  unavailable: (theme: Theme) => ({
    position: "absolute" as "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colorTokens.N[30],
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
